import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient';

/**
 * useEmailsHub — hook unificado para acessar emails em 3 contextos diferentes.
 *
 * Modos:
 *   - { modo: 'empresa', empresaId }  → todos os emails onde a empresa aparece
 *     (como empresa_id, como empresa_proprietaria_id, ou via domínio dos contatos)
 *   - { modo: 'oferta', ofertaId }    → emails vinculados a uma oferta específica
 *   - { modo: 'busca', termo }        → busca full-text em from/to/cc/subject/body
 *
 * Cada email retornado traz `_papeis` — array de strings descrevendo por que
 * ele apareceu naquele contexto. Ex: ['compradora', 'thread_compartilhada'].
 */
export const useEmailsHub = ({ modo, empresaId, ofertaId, termo }) => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totais, setTotais] = useState({ total: 0 });

  const fetchEmails = useCallback(async () => {
    if (modo === 'empresa' && !empresaId) return;
    if (modo === 'oferta' && !ofertaId) return;
    if (modo === 'busca' && (!termo || termo.trim().length < 2)) return;

    setLoading(true);
    setError(null);

    try {
      let resultado = [];

      if (modo === 'empresa') {
        // Query 1: emails com empresa_id OU empresa_proprietaria_id = empresaId
        const { data: diretos, error: err1 } = await supabase
          .from('emails_gmail')
          .select(`
            id, gmail_thread_id, from_email, to_emails, cc_emails, subject, 
            body_text, data_envio, direcao, remetente_tipo, tipo_conteudo,
            empresa_id, empresa_proprietaria_id, area_mencionada_id,
            oferta_id, acao_humana, classificacao_status,
            empresas:empresa_id (id, empresa),
            empresa_proprietaria:empresa_proprietaria_id (id, empresa),
            areas:area_mencionada_id (id, nome),
            ofertas:oferta_id (id, nome)
          `)
          .or(`empresa_id.eq.${empresaId},empresa_proprietaria_id.eq.${empresaId}`)
          .order('data_envio', { ascending: false })
          .limit(500);

        if (err1) throw err1;

        // Query 2: domínios da empresa — usado para detectar papéis
        const { data: dominios } = await supabase
          .from('dominios_empresa')
          .select('dominio, tipo_empresa')
          .eq('empresa_id', empresaId);

        const dominiosEmpresa = new Set((dominios || []).map(d => d.dominio));

        // Enriquece cada email com _papeis (por que ele está aqui)
        resultado = (diretos || []).map(em => {
          const papeis = [];
          if (em.empresa_id === empresaId) papeis.push('compradora');
          if (em.empresa_proprietaria_id === empresaId) papeis.push('proprietaria');
          
          const fromDom = (em.from_email || '').split('@')[1];
          if (fromDom && dominiosEmpresa.has(fromDom)) papeis.push('remetente');
          
          const destDoms = [...(em.to_emails || []), ...(em.cc_emails || [])]
            .map(e => (e || '').split('@')[1])
            .filter(Boolean);
          if (destDoms.some(d => dominiosEmpresa.has(d))) papeis.push('destinatario');
          
          return { ...em, _papeis: papeis };
        });

      } else if (modo === 'oferta') {
        const { data, error: err } = await supabase
          .from('emails_gmail')
          .select(`
            id, gmail_thread_id, from_email, to_emails, cc_emails, subject, 
            body_text, data_envio, direcao, remetente_tipo, tipo_conteudo,
            empresa_id, empresa_proprietaria_id, area_mencionada_id, 
            oferta_id, acao_humana, classificacao_status,
            empresas:empresa_id (id, empresa),
            empresa_proprietaria:empresa_proprietaria_id (id, empresa),
            areas:area_mencionada_id (id, nome),
            ofertas:oferta_id (id, nome)
          `)
          .eq('oferta_id', ofertaId)
          .order('data_envio', { ascending: false });

        if (err) throw err;
        resultado = (data || []).map(em => ({ ...em, _papeis: ['oferta'] }));

      } else if (modo === 'busca') {
        const termoLimpo = termo.trim();
        // Busca em from_email, subject, body_text (ILIKE = case-insensitive)
        // to_emails e cc_emails são arrays — usamos any() na cláusula
        const termoLower = termoLimpo.toLowerCase();
        const padrao = `%${termoLimpo}%`;
        
        const { data, error: err } = await supabase
          .from('emails_gmail')
          .select(`
            id, gmail_thread_id, from_email, to_emails, cc_emails, subject, 
            body_text, data_envio, direcao, remetente_tipo, tipo_conteudo,
            empresa_id, empresa_proprietaria_id, area_mencionada_id, 
            oferta_id, acao_humana, classificacao_status,
            empresas:empresa_id (id, empresa),
            empresa_proprietaria:empresa_proprietaria_id (id, empresa),
            areas:area_mencionada_id (id, nome),
            ofertas:oferta_id (id, nome)
          `)
          .or(`from_email.ilike.${padrao},subject.ilike.${padrao},body_text.ilike.${padrao}`)
          .order('data_envio', { ascending: false })
          .limit(100);

        if (err) throw err;
        
        // Complemento client-side: buscar também em to_emails/cc_emails
        // (PostgREST não suporta filter em array de text via OR dinâmico facilmente)
        const baseResultado = data || [];
        
        // Busca secundária: emails onde algum to/cc contém o termo
        // Como já limitamos a 100 na principal, pulamos a busca de array por performance
        // Se o usuário quiser busca mais precisa, é adição futura
        resultado = baseResultado.map(em => {
          const papeis = [];
          const t = termoLower;
          if ((em.from_email || '').toLowerCase().includes(t)) papeis.push('from');
          if ((em.subject || '').toLowerCase().includes(t)) papeis.push('assunto');
          if ((em.body_text || '').toLowerCase().includes(t)) papeis.push('corpo');
          if ((em.to_emails || []).some(e => (e || '').toLowerCase().includes(t))) papeis.push('para');
          if ((em.cc_emails || []).some(e => (e || '').toLowerCase().includes(t))) papeis.push('cc');
          return { ...em, _papeis: papeis };
        });
      }

      setEmails(resultado);
      setTotais({ total: resultado.length });
    } catch (err) {
      console.error('useEmailsHub:', err);
      setError(err.message);
      setEmails([]);
    } finally {
      setLoading(false);
    }
  }, [modo, empresaId, ofertaId, termo]);

  useEffect(() => { fetchEmails(); }, [fetchEmails]);

  // Agrupamento por oferta — útil pra visão "empresa"
  const emailsPorOferta = useMemo(() => {
    const map = new Map();
    emails.forEach(em => {
      const key = em.oferta_id || 'sem_oferta';
      if (!map.has(key)) {
        map.set(key, {
          oferta_id: em.oferta_id,
          oferta_nome: em.ofertas?.nome || (em.oferta_id ? 'Oferta desconhecida' : '(sem vínculo)'),
          emails: [],
        });
      }
      map.get(key).emails.push(em);
    });
    return Array.from(map.values()).sort((a, b) => b.emails.length - a.emails.length);
  }, [emails]);

  // Agrupamento por thread Gmail
  const emailsPorThread = useMemo(() => {
    const map = new Map();
    emails.forEach(em => {
      const key = em.gmail_thread_id || em.id;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(em);
    });
    return Array.from(map.entries())
      .map(([threadId, ems]) => ({
        threadId,
        subjectBase: (ems[0].subject || '').replace(/^(Re|RE|Enc|ENC|Fwd|FWD|FW):\s*/gi, '').trim(),
        emails: ems.sort((a, b) => new Date(a.data_envio) - new Date(b.data_envio)),
        primeiroEmail: ems[ems.length - 1], // o mais antigo (emails são DESC)
        ultimoEmail: ems[0],
      }))
      .sort((a, b) => new Date(b.ultimoEmail.data_envio) - new Date(a.ultimoEmail.data_envio));
  }, [emails]);

  return {
    emails,
    emailsPorOferta,
    emailsPorThread,
    totais,
    loading,
    error,
    refetch: fetchEmails,
  };
};
